import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { PaymentCheckoutWizardService } from '@app/account/shared/payment-checkout-wizard/payment-checkout-wizard.service';
import { ChangableComponent } from '@app/models/changable.component';
import {
  convertUserScheduleToDatetimePickerEvent,
  IDatetimePickerPeriod,
  IDatetimePickerSelection,
} from '@core/components/datetime-picker/models';
import { logger } from '@core/helpers/logger';
import { BillingService, ConfigService, PlatformService, ScheduleService } from '@core/services';
import {
  CurrencyEnum,
  ICurrencyAmount,
  IUserScheduleFilter,
  User,
  UserProfile,
  UserService,
  SignupScheduleDigestRequest,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { takeUntil } from 'rxjs';
import {ErrorNotificationService} from "@shared/error-notification/error-notification.service";

@Component({
  selector: 'app-user-service-datetime',
  templateUrl: './datetime.component.html',
  styleUrls: ['./datetime.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserServiceDatetimeComponent extends ChangableComponent implements OnInit {
  @Input()
  public set profile(user: Partial<UserProfile>) {
    this.profileData = user;
  }

  @Input()
  public set teacher(user: Partial<User>) {
    this.teacherData = user;
  }

  @Input()
  public set me(me: User) {
    this._me = me;
  }

  @Output() public timeSelected = new EventEmitter<boolean>();
  @Output() public closeFull = new EventEmitter<boolean>();

  public profileData: Partial<UserProfile>;
  public teacherData: Partial<User>;
  public _me: User;
  public teacherDontHaveTime: boolean;
  private _userServicePrice: ICurrencyAmount;

  public min_date: Date;
  public max_date: Date;

  public constructor(
    public errorNotificationService: ErrorNotificationService,
    public deviceService: DeviceDetectorService,
    protected configService: ConfigService,
    protected billingService: BillingService,
    protected scheduleService: ScheduleService,
    public models: PaymentCheckoutWizardService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  public ngOnInit() {
    this.loadTeacherScheduleDigest();
    this.models.state$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (step) => {
        if (!this.models.time) {
          this.loadUserServicePrice();
        } else {
          this.models.addTime(this.models.time);
        }
        this.detectChanges();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      }
    });
  }

  protected loadTeacherScheduleDigest() {
    // загрузить информацию о ближайших событиях преподавателя
    this.models.setCalendarLoading();
    const request = new SignupScheduleDigestRequest({
      account_id: this.models.teacher?.id,
      user_service_id: this.models.content instanceof UserService ? this.models.content.id : null,
    });
    this.scheduleService
      .getScheduleDigest(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (digest) => {
          logger.log('UserServiceCheckoutWizardComponent:loadTeacherScheduleDigest()', digest);
          if (digest && digest.min_date && digest.max_date) {
            /** @todo УБрать прямое обращение к this.state.calendar */
            this.teacherDontHaveTime = false;
            this.min_date = digest.min_date;
            this.max_date = digest.max_date;
            this.models.calendar.filter.date_from = this.min_date;
            this.models.calendar.filter.date_to = this.max_date;
            this.loadSchedule();
          } else {
            logger.error('У преподавателя нет свободного времени', digest);
            this.models.setCalendarLoading(false);
            this.teacherDontHaveTime = true;
            this.models.calendar.events = [];
          }
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.models.setCalendarLoading(false);
        }
      });
    this.detectChanges();
  }

  protected loadSchedule() {
    this.models.setCalendarLoading();
    const scheduleFilter: Partial<IUserScheduleFilter> = {
      ...this.models.calendar.filter,
      account_id: this.models?.teacher?.id,
      user_service_id: this.models?.content?.id,
    };
    this.scheduleService
      .getCombinedSchedule(scheduleFilter)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.models.setCalendarLoading(false);
          // DEBUG this.schedule = response;
          this.models.calendar.events = convertUserScheduleToDatetimePickerEvent(response).filter(
            (e) => e.status !== 'busy',
          );
          this.detectChanges();
        },
        error: (err: any) => {
          this.models.calendar.loading = false;
          logger.error('UserServiceCheckoutWizardComponent:loadSchedule()', err);
        }
      });
  }

  public onCalendarPeriodChanged(period: any) {
    const { start, end } = period as IDatetimePickerPeriod;
    this.models.calendar.filter.date_from = start;
    this.models.calendar.filter.date_to = end;
    this.loadSchedule();
  }

  public onTimeSelected(selection: any) {
    this.models.setTimes((selection as IDatetimePickerSelection).time);
    this.timeSelected.emit(true);
  }

  public closeWindow() {
    this.closeFull.emit(true);
  }

  public formatCurrencyAmount(amount: number, currency_id?: CurrencyEnum): string {
    currency_id = currency_id || CurrencyEnum.usd;
    return this.billingService.formatAmount(amount, currency_id);
  }

  public get servicePrice(): ICurrencyAmount {
    return this._userServicePrice;
  }

  private loadUserServicePrice() {
    if (!this.models.content) {
      return;
    }

    const content = this.models.content as UserService;

    this.billingService
      .getPriceTier$(content.price_tier)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (price) => {
          this._userServicePrice = price;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        }
      });
  }

  public get currency_id(): number {
    return this._me && this._me.currency_id;
  }
}
